import React, {useEffect, useState, useRef, useMemo} from 'react';
import {Pagination, message} from 'antd';
import {useMutation} from '@apollo/client';
import {useTranslation} from 'react-i18next';
import {SorterFilterInitialValue} from "./config/sorterFilterOptions";
import {SorterType} from "./graphql/queries/getSorterListing";
import {useSorterQuery, SorterFiltersType} from "./hooks/useSorterQuery";
import SorterFilter from "./components/SorterFilter";
import SorterColumns from "./components/SorterColumns";
import SorterModal from "./components/SorterModal";
import {DELETE_SORTER, DeleteSorterResponse, DeleteSorterVariables} from './graphql/mutations/deleteSorter';
import {CLEAR_SORTER_BAG_IDS} from './graphql/mutations/clearSorterBagIds';
import {PRINT_SORTER_CHUTE_LABELS} from './graphql/mutations/printSorterChuteLabels';
import {printPdfFromBase64} from '../bags/utils/printUtils';
import {useWarehouseContext} from '../../../contexts/WarehouseContext';
import {useWarehouseOptions} from '../../../hooks/useWarehouseOptions';

const SorterPage: React.FC = () => {
    const {t} = useTranslation();
    const {currentWarehouse} = useWarehouseContext();
    
    // 初始化过滤条件时带上当前仓库ID
    const initialFilters = useMemo(() => ({
        ...SorterFilterInitialValue,
        warehouseId: currentWarehouse?.id ? Number(currentWarehouse.id) : undefined
    }), [currentWarehouse?.id]);
    
    const [tempFilters, setTempFilters] = useState<SorterFiltersType>(initialFilters);
    const [filters, setFilters] = useState<SorterFiltersType>(initialFilters);
    const initializedRef = useRef(false);
    const [currentPage, setCurrentPage] = useState(1);
    const [pageSize, setPageSize] = useState(20);
    const [modalVisible, setModalVisible] = useState(false);
    const [editingRecord, setEditingRecord] = useState<SorterType | undefined>(undefined);

    // 从后端获取真实的仓库数据（包括所有 Hub 和 Warehouse）
    const {warehouseOptions: rawWarehouseOptions, loading: warehouseLoading} = useWarehouseOptions('ALL');

    // 转换仓库选项格式
    const warehouseOptions = useMemo(() => {
        return rawWarehouseOptions.map(w => ({
            value: w.id,
            label: w.name
        }));
    }, [rawWarehouseOptions]);

    const {
        sorterData,
        loading,
        refetch,
        totalElements,
        buildQueryVariables
    } = useSorterQuery(filters, currentPage, pageSize, false);

    const [deleteSorter] = useMutation<DeleteSorterResponse, DeleteSorterVariables>(DELETE_SORTER);
    const [clearBagIds] = useMutation(CLEAR_SORTER_BAG_IDS);
    const [printChuteLabels] = useMutation(PRINT_SORTER_CHUTE_LABELS);

    // 当仓库切换时，更新过滤条件
    useEffect(() => {
        if (!initializedRef.current) {
            setTempFilters(initialFilters);
            setFilters(initialFilters);
            initializedRef.current = true;
        } else if (currentWarehouse?.id) {
            // 仓库切换时更新 warehouseId，保留其他过滤条件
            const newWarehouseId = Number(currentWarehouse.id);
            setTempFilters(prev => ({ ...prev, warehouseId: newWarehouseId }));
            setFilters(prev => ({ ...prev, warehouseId: newWarehouseId }));
            setCurrentPage(1);
        }
    }, [currentWarehouse?.id, initialFilters]);

    const handleResetFilters = () => {
        // 重置时保留当前仓库ID
        const resetFilters = {
            ...SorterFilterInitialValue,
            warehouseId: currentWarehouse?.id ? Number(currentWarehouse.id) : undefined
        };
        setTempFilters(resetFilters);
        setFilters(resetFilters);
        setCurrentPage(1);
    };

    const handleFilter = () => {
        setFilters(tempFilters);
        setCurrentPage(1);
    };

    useEffect(() => {
        refetch(buildQueryVariables());
    }, [filters, currentPage, pageSize]);

    const handlePageChange = (page: number, pageSize: number) => {
        setCurrentPage(page);
        setPageSize(pageSize);
    };

    const handleAddSorter = () => {
        setEditingRecord(undefined);
        setModalVisible(true);
    };

    const handleEdit = (record: SorterType) => {
        setEditingRecord(record);
        setModalVisible(true);
    };

    const handleDelete = async (id: string) => {
        try {
            await deleteSorter({
                variables: { id: parseInt(id) }
            });
            message.success(t('wms.sorter.messages.deleteSuccess'));
            refetch(buildQueryVariables());
        } catch (error: any) {
            console.error('Failed to delete sorter:', error);

            // 提取错误消息
            let errorMessage = t('wms.sorter.messages.deleteFailed');

            // 优先检查 extensions.messageKey
            if (error?.graphQLErrors?.[0]?.extensions?.messageKey) {
                const messageKey = error.graphQLErrors[0].extensions.messageKey;
                errorMessage = t(messageKey) || messageKey;
            } else if (error?.graphQLErrors?.[0]?.message) {
                errorMessage = error.graphQLErrors[0].message;
            } else if (error?.message) {
                errorMessage = error.message;
            }

            message.error(errorMessage);
        }
    };

    const handleResetBags = async (id: string) => {
        try {
            await clearBagIds({
                variables: { sorterId: parseInt(id) }
            });
            message.success(t('wms.sorter.messages.resetBagsSuccess'));
            refetch(buildQueryVariables());
        } catch (error: any) {
            console.error('Reset bags error:', error);

            // 解析错误信息
            let errorMessage = t('wms.sorter.messages.resetBagsFailed');

            // 优先检查 extensions.messageKey
            if (error?.graphQLErrors?.[0]?.extensions?.messageKey) {
                const messageKey = error.graphQLErrors[0].extensions.messageKey;
                errorMessage = t(messageKey) || messageKey;
            } else if (error?.graphQLErrors?.[0]?.message) {
                errorMessage = error.graphQLErrors[0].message;
            } else if (error?.message) {
                errorMessage = error.message;
            }

            message.error(errorMessage);
        }
    };

    const handlePrintChuteLabels = async (record: SorterType) => {
        try {
            const res = await printChuteLabels({
                variables: { sorterId: parseInt(record.id) }
            });
            const result = res.data?.printSorterChuteLabels;
            if (result?.success && result.pdfBase64) {
                printPdfFromBase64(result.pdfBase64, `chute_labels_${record.sorterCode || record.id}_${Date.now()}.pdf`);
                message.success(t('wms.sorter.messages.printChuteLabelsSuccess'));
            } else {
                message.error(result?.message || t('wms.sorter.messages.printChuteLabelsFailed'));
            }
        } catch (error: any) {
            console.error('Print chute labels error:', error);

            let errorMessage = t('wms.sorter.messages.printChuteLabelsFailed');
            if (error?.graphQLErrors?.[0]?.extensions?.messageKey) {
                const messageKey = error.graphQLErrors[0].extensions.messageKey;
                errorMessage = t(messageKey) || messageKey;
            } else if (error?.graphQLErrors?.[0]?.message) {
                errorMessage = error.graphQLErrors[0].message;
            } else if (error?.message) {
                errorMessage = error.message;
            }

            message.error(errorMessage);
        }
    };

    const handleModalSuccess = () => {
        setModalVisible(false);
        setEditingRecord(undefined);
        refetch(buildQueryVariables());
    };

    const handleModalCancel = () => {
        setModalVisible(false);
        setEditingRecord(undefined);
    };

    return (
        <div className="bg-white rounded-lg shadow-sm p-6">
            {/* 筛选条件 */}
            <SorterFilter
                filters={tempFilters}
                setFilters={setTempFilters}
                handleFilter={handleFilter}
                handleResetFilters={handleResetFilters}
                onAddSorter={handleAddSorter}
            />

            {/* 分拣机列表 */}
            <SorterColumns
                sorterData={sorterData}
                loading={loading}
                onEdit={handleEdit}
                onDelete={handleDelete}
                onResetBags={handleResetBags}
                onPrintChuteLabels={handlePrintChuteLabels}
            />

            {/* 分页 */}
            <div className="flex justify-between items-center mt-4">
                <div className="text-gray-500 text-sm">{t('wms.sorter.pagination.total', {total: totalElements})}</div>
                <Pagination
                    current={currentPage}
                    total={totalElements}
                    pageSize={pageSize}
                    size="small"
                    showSizeChanger
                    onChange={handlePageChange}
                />
            </div>

            {/* 新增/编辑模态框 */}
            <SorterModal
                visible={modalVisible}
                onCancel={handleModalCancel}
                onSuccess={handleModalSuccess}
                editingRecord={editingRecord}
                warehouseOptions={warehouseOptions}
                defaultWarehouseId={currentWarehouse?.id}
            />
        </div>
    );
};

export default SorterPage;

